import { useState } from 'react';
import { Option, WithBillId } from '@mews-commander/types';
import { Divider, DropDownButton, DropdownItem, IconName, Input, InputValue, Stack } from '@optimus-web/core';
import { LocalizedText, lt, useFeatureFlag, useScope } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { getCurrentEnterpriseProductServiceOptions } from '@mews-commander/legacy-entities/src/selectors';
import { getBill } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { openProductOrderModal } from '../../../actions/productOrders';
import { isAnythingSelected } from '../../../ducks/billItems';
import { useLinkProductsToReservationsEnabled } from '../../../hooks/useLinkProductsToReservationsEnabled';
import { PolymorphicProductDialog } from '../../../../productServiceOrder/components/ProductServiceOrderDialog/AddProduct/PolymorphicProductDialog';
import { getReservationIdsByBillIds } from '../../../ducks/reservations';

const useFilteredOptions = (options: Option<String>[], searchQuery: InputValue) => {
    if (!searchQuery) {
        return options;
    }

    return options.filter(({ label }) => String(label)?.toLowerCase().includes(String(searchQuery).toLowerCase()));
};

export const AddServiceOrderButton = ({ billId }: WithBillId) => {
    const dispatch = useCommanderThunkDispatch();
    const [searchQuery, setSearchQuery] = useState<InputValue>('');
    const scope = useScope() ?? '';
    const disabled = useCommanderSelector((state) => isAnythingSelected(state, { scope }));
    const bill = useCommanderSelector((state) => getBill(state, { id: billId }));
    const billReservations = useCommanderSelector((state) => getReservationIdsByBillIds(state, { billIds: [billId] }));
    const preventProductServiceOutsideReservationEnabled = useFeatureFlag(
        FeatureFlag.preventingProductServiceOutsideOfReservationWindow
    );

    const linkProductsToReservationsEnabled = useLinkProductsToReservationsEnabled();

    const [activeServiceId, setActiveServiceId] = useState<string | null>(null);

    const createServiceOrder = (serviceId: string | undefined) =>
        dispatch(openProductOrderModal({ billId, serviceId, scope }));

    const createServiceOrderInDialog = (serviceId: string | null) => {
        setActiveServiceId(serviceId);
    };
    const allOptions = useCommanderSelector((state) => getCurrentEnterpriseProductServiceOptions(state));
    const options = useFilteredOptions(allOptions, searchQuery);
    const showSearchControl = linkProductsToReservationsEnabled && allOptions.length >= 5;

    return (
        <>
            <DropDownButton
                icon={IconName.Add}
                variant="ghost"
                label={<LocalizedText textKey={TextKey.AddProduct} />}
                disabled={disabled}
            >
                {showSearchControl && (
                    <Stack vertical>
                        <Input
                            name="search-product"
                            type="search"
                            indicativeIcon={IconName.Search}
                            placeholder={lt(TextKey.Search)}
                            onClick={(e) => e.stopPropagation()}
                            size="large"
                            value={searchQuery}
                            onChange={(_, sq) => setSearchQuery(sq)}
                            autoFocus
                        />
                        <Divider />
                    </Stack>
                )}
                {options.map(({ value, label }) => (
                    <DropdownItem
                        label={label}
                        value={String(value)}
                        onClick={(_, serviceId) =>
                            (linkProductsToReservationsEnabled ? createServiceOrderInDialog : createServiceOrder)(
                                String(serviceId)
                            )
                        }
                        key={String(value)}
                    />
                ))}
            </DropDownButton>

            {linkProductsToReservationsEnabled && activeServiceId && bill && (
                <PolymorphicProductDialog
                    scopedReservationIds={preventProductServiceOutsideReservationEnabled ? billReservations : []}
                    onClose={() => setActiveServiceId(null)}
                    serviceId={activeServiceId}
                    billId={bill.isVirtual ? undefined : billId}
                    accountId={bill.ownerId}
                    active
                />
            )}
        </>
    );
};
